import React from 'react';
import { ProgressBar, Icon, Popover } from '@brighterion/br-ui';
import { NumberService } from 'Services';
import DashOrEllipText from 'Components/DashOrEllipText';
import { handleOnClickEvent, handleKeyEvent, OnClickEvent, KeyEvent } from 'Utils';
import { Link } from 'react-router-dom';
import { Cell } from 'react-table';
import * as _ from 'lodash';
import styles from './index.css';

export function renderLinkButton(value: string, renderOptions: LinkRenderOptions) {
    const valueArr = Array.isArray(value) ? value : [value];
    const { to, openNewWindow, newWindowParams, isString, iconName, exceptions } = renderOptions;
    if (exceptions?.includes(value)) return <>{value}</>;

    const localeValue = _.isNaN(Number(value)) || isString ? value : NumberService.getLocaleNumber(Number(value));

    try {
        // replace $0, $1, $2, $3 from string from array positions 0, 1, 2, 3
        const regexExp = /\$\d+/g;
        let valueFromArr;
        const regexedLink = to.replace(regexExp, (matched: string) => {
            const removeDollarSign = Number.parseInt(matched.replace('$', ''), 10);
            valueFromArr = valueArr[removeDollarSign];
            return valueFromArr;
        });

        return (
            <Link
                className='ButtonLink__link ButtonLink__small'
                onClick={(e) => {
                    e.stopPropagation();
                    if (openNewWindow) {
                        e.preventDefault();
                        window?.open(regexedLink, regexedLink, newWindowParams);
                    }
                }}
                to={regexedLink}
                data-testid={`table-cell-link-${valueFromArr}`}
            >
                {iconName && <Icon className={styles.linkIcon} name={iconName} size='xlg' />} {localeValue}
            </Link>
        );
    } catch (e) {
        return <>{localeValue}</>;
    }
}

export function renderProgressBar(value: string, renderOptions: ProgressBarRenderOptions) {
    const {
        className,
        fillAreaClass,
        labelClassName,
        percentDecimals = 0,
        valueIsPercent,
        labelOverride
    } = renderOptions;
    const roundedValue = valueIsPercent ? Number(value) : _.round(Number(value) * 0.01, percentDecimals + 2);
    return (
        <ProgressBar
            className={className}
            fillAreaClass={fillAreaClass}
            labelClassName={labelClassName}
            inlineLabel
            labelPosition='back'
            progress={roundedValue}
            percentDecimals={percentDecimals}
            valueIsPercent={valueIsPercent}
            labelOverride={labelOverride}
        />
    );
}

export function renderModifiedIcon(value: string) {
    if (value === 'new') return <Icon name='table-new' size='sm' />;
    if (value === 'modified') return <Icon name='table-updated' size='sm' />;
    return null;
}

export function renderDefault<D extends object = {}>(cell: Cell<D>) {
    const { value } = cell;
    return <DashOrEllipText rawValue={value} />;
}

export function renderTrashIcon(value: number, renderOptions: TrashIconRenderOptions, row: object = {}) {
    const handleAction = <T,>(event: OnClickEvent<T> | KeyEvent<T>) => renderOptions?.onClick?.(value, event, row);
    return (
        <div className={styles.trash}>
            <Icon
                className={styles.orangeIcon}
                name='trash'
                size='md'
                tabIndex={0}
                onClick={handleOnClickEvent(handleAction)}
                onKeyDown={handleKeyEvent(handleAction)}
            />
        </div>
    );
}

export function renderCommentIcon(value: string) {
    return (
        <div className={styles.comment}>
            <Popover content={value} contentClassName={styles.commentPopperContent} showOnHover showArrow>
                <Icon name='comment' size='md' />
            </Popover>
        </div>
    );
}

export function renderTitleCase(value: string, renderOptions?: TitleCaseRenderOptions) {
    if (renderOptions && renderOptions.replace[value as keyof typeof renderOptions.replace]) {
        return <>{renderOptions.replace[value as keyof typeof renderOptions.replace]}</>;
    }
    return (
        <>
            {value
                .toLowerCase()
                .replace(/(^\w)/g, (g: string) => g[0].toUpperCase())
                .replace(/([-_]\w)/g, (g: string) => ` ${g[1].toUpperCase()}`)
                .trim()}
        </>
    );
}
